/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  useRef,
} from 'react';

interface KeyboardContextValue {
  isKeyboardVisible: boolean;
  keyboardHeight: number;
  isInputFocused: boolean;
}

const KeyboardContext = createContext<KeyboardContextValue>({
  isKeyboardVisible: false,
  keyboardHeight: 0,
  isInputFocused: false,
});

export function useKeyboardContext() {
  return useContext(KeyboardContext);
}

// Detect if device is mobile/touch
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Check for VirtualKeyboard API support (Chrome Android 94+)
const hasVirtualKeyboardAPI = () => {
  if (typeof navigator === 'undefined') return false;
  return 'virtualKeyboard' in navigator;
};

interface KeyboardProviderProps {
  children: ReactNode;
}

/**
 * Global keyboard state provider.
 * Uses multiple detection methods for cross-browser compatibility:
 * 1. VirtualKeyboard API (Chrome Android 94+) - most reliable
 * 2. VisualViewport API (iOS Safari, older Android) - viewport resize detection
 * 3. Focus-based fallback for browsers without viewport APIs
 */
export function KeyboardProvider({ children }: KeyboardProviderProps) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const animationFrameRef = useRef<number | null>(null);
  const focusedElementRef = useRef<Element | null>(null);
  const isMobile = useRef(isMobileDevice());
  const initialWindowHeight = useRef<number>(0);
  const hasViewportAPI = useRef(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const keyboardWasOpen = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Reset keyboard state - called when keyboard closes
  const resetKeyboardState = useCallback(() => {
    focusedElementRef.current = null;
    setIsInputFocused(false);
    setKeyboardHeight(0);
    stopPolling();
    document.body.classList.remove('keyboard-open');
    document.documentElement.style.setProperty('--keyboard-height', '0px');
  }, [stopPolling]);

  // Update keyboard state based on height
  const updateKeyboardFromHeight = useCallback((height: number) => {
    setKeyboardHeight(height);
    document.documentElement.style.setProperty('--keyboard-height', `${height}px`);

    // Consider keyboard open if height is significant (> 100px)
    if (height > 100) {
      document.body.classList.add('keyboard-open');
    } else {
      document.body.classList.remove('keyboard-open');
    }
  }, []);

  // Check current viewport height (used for polling and event handlers)
  const checkViewportHeight = useCallback(() => {
    // Guard: skip keyboard detection when the page is pinch-zoomed.
    // Viewport height changes during zoom are NOT from the virtual keyboard,
    // and processing them corrupts the baseline height (initialWindowHeight)
    // which causes the navigation to vanish permanently.
    const scale = window.visualViewport?.scale ?? 1;
    if (Math.abs(scale - 1) > 0.1) {
      // If we previously thought the keyboard was open due to a zoom event,
      // clear that false positive so the nav comes back.
      if (keyboardWasOpen.current) {
        keyboardWasOpen.current = false;
        updateKeyboardFromHeight(0);
      }
      return;
    }

    // With resizes-content mode, window.innerHeight changes when keyboard opens/closes
    const currentWindowHeight = window.innerHeight;
    const baseHeight = initialWindowHeight.current;

    // If no base height stored or current is larger, update it
    // (Current might be larger if we captured during keyboard open)
    if (!baseHeight || currentWindowHeight > baseHeight) {
      initialWindowHeight.current = currentWindowHeight;
    }

    // Calculate how much smaller the window is (keyboard height estimate)
    let heightDiff = Math.max(0, initialWindowHeight.current - currentWindowHeight);

    // Safety net: if no text input is focused, the virtual keyboard cannot be open.
    // Any residual height difference is from a zoom or orientation change, not a keyboard.
    if (!focusedElementRef.current) {
      // Recalibrate the baseline so stale values from prior zoom don't persist
      if (heightDiff > 0) {
        initialWindowHeight.current = currentWindowHeight;
      }
      heightDiff = 0;
    }

    // Track keyboard state changes for debugging
    const wasOpen = keyboardWasOpen.current;
    const isOpen = heightDiff > 100;

    if (wasOpen && !isOpen) {
      // Keyboard just closed - also check if focus moved away from input
      const activeElement = document.activeElement;
      const isStillTextInput =
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.getAttribute('contenteditable') === 'true');

      // If keyboard closed but input still focused, blur it
      // (This handles Android back button)
      if (isStillTextInput && focusedElementRef.current === activeElement) {
        (activeElement as HTMLElement).blur();
      }
    }

    keyboardWasOpen.current = isOpen;
    updateKeyboardFromHeight(heightDiff);
  }, [updateKeyboardFromHeight]);

  // Start polling for viewport changes (catches Android back button)
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;

    pollingIntervalRef.current = setInterval(() => {
      checkViewportHeight();
    }, 200); // Check every 200ms
  }, [checkViewportHeight]);

  // VirtualKeyboard API handler (Chrome Android 94+)
  const handleVirtualKeyboardGeometryChange = useCallback(() => {
    // @ts-expect-error - VirtualKeyboard API types not in TypeScript yet
    const vk = navigator.virtualKeyboard;
    if (!vk) return;

    const { height } = vk.boundingRect;
    updateKeyboardFromHeight(height);
  }, [updateKeyboardFromHeight]);

  // Window resize handler - works with resizes-content mode
  const handleWindowResize = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      checkViewportHeight();
    });
  }, [checkViewportHeight]);

  // VisualViewport API handler (iOS Safari, older Android)
  const handleVisualViewportResize = useCallback(() => {
    if (!window.visualViewport) return;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      checkViewportHeight();
    });
  }, [checkViewportHeight]);

  // Track focused input elements
  const handleFocusIn = useCallback(
    (e: FocusEvent) => {
      const target = e.target as Element;
      const isInputElement =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('contenteditable') === 'true';

      if (isInputElement) {
        // Check if it's a text-entry input (not button, checkbox, etc.)
        const inputType = (target as HTMLInputElement).type;
        const isTextInput =
          !inputType ||
          ['text', 'email', 'password', 'search', 'tel', 'url', 'number'].includes(inputType) ||
          target.tagName === 'TEXTAREA';

        if (isTextInput) {
          focusedElementRef.current = target;
          setIsInputFocused(true);

          // Start polling for viewport changes (catches Android back button)
          if (isMobile.current) {
            startPolling();
          }

          // Only add keyboard-open if we don't have viewport API (fallback)
          if (!hasViewportAPI.current) {
            document.body.classList.add('keyboard-open');
          }

          // Scroll the input into view after a short delay
          // Clear any existing scroll timeout to prevent memory leaks
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
          }
          scrollTimeoutRef.current = setTimeout(() => {
            if (focusedElementRef.current) {
              (focusedElementRef.current as HTMLElement).scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 300);
        }
      }
    },
    [startPolling]
  );

  const handleFocusOut = useCallback(
    (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Element | null;

      // Check if focus is moving to another text input
      if (relatedTarget) {
        const isInputElement =
          relatedTarget.tagName === 'INPUT' ||
          relatedTarget.tagName === 'TEXTAREA' ||
          relatedTarget.getAttribute('contenteditable') === 'true';

        if (isInputElement) {
          const inputType = (relatedTarget as HTMLInputElement).type;
          const isTextInput =
            !inputType ||
            ['text', 'email', 'password', 'search', 'tel', 'url', 'number'].includes(inputType) ||
            relatedTarget.tagName === 'TEXTAREA';

          if (isTextInput) {
            focusedElementRef.current = relatedTarget;
            return; // Keep keyboard state
          }
        }
      }

      // Focus left all text inputs
      focusedElementRef.current = null;
      setIsInputFocused(false);

      // Only reset keyboard if we don't have viewport API (fallback)
      if (!hasViewportAPI.current) {
        resetKeyboardState();
      }
    },
    [resetKeyboardState]
  );

  // Handle touch outside inputs - blur to dismiss keyboard
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!focusedElementRef.current) return;

    const target = e.target as Element;
    const isInputElement =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.getAttribute('contenteditable') === 'true' ||
      target.closest('input, textarea, [contenteditable="true"]');

    // If touch is outside any input, blur the current input
    if (!isInputElement) {
      setTimeout(() => {
        if (focusedElementRef.current && document.activeElement === focusedElementRef.current) {
          (focusedElementRef.current as HTMLElement).blur();
        }
      }, 10);
    }
  }, []);

  useEffect(() => {
    // Initialize
    document.documentElement.style.setProperty('--keyboard-height', '0px');
    isMobile.current = isMobileDevice();

    // Store initial window height for comparison (with resizes-content, this changes)
    initialWindowHeight.current = window.innerHeight;

    // Set up keyboard detection based on available APIs
    // We use multiple methods for redundancy

    // Method 1: Window resize (works best with resizes-content mode)
    window.addEventListener('resize', handleWindowResize);
    hasViewportAPI.current = true;

    // Method 2: VirtualKeyboard API (Chrome Android 94+)
    if (hasVirtualKeyboardAPI()) {
      // @ts-expect-error - VirtualKeyboard API types not in TypeScript yet
      const vk = navigator.virtualKeyboard;
      vk.addEventListener('geometrychange', handleVirtualKeyboardGeometryChange);
    }

    // Method 3: VisualViewport API (iOS Safari, all Android)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      window.visualViewport.addEventListener('scroll', handleVisualViewportResize);
    }

    // Always listen for focus events
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);

    // Listen for touch events on mobile to detect taps outside input
    if (isMobile.current) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);

      // Clean up VirtualKeyboard API
      if (hasVirtualKeyboardAPI()) {
        try {
          // @ts-expect-error - VirtualKeyboard API types not in TypeScript yet
          navigator.virtualKeyboard?.removeEventListener(
            'geometrychange',
            handleVirtualKeyboardGeometryChange
          );
        } catch {
          // Ignore errors during cleanup
        }
      }

      // Clean up VisualViewport API
      if (window.visualViewport) {
        try {
          window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
          window.visualViewport.removeEventListener('scroll', handleVisualViewportResize);
        } catch {
          // Ignore errors during cleanup
        }
      }

      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);

      if (isMobile.current) {
        document.removeEventListener('touchstart', handleTouchStart);
      }

      document.body.classList.remove('keyboard-open');

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Clean up scroll timeout to prevent memory leaks
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      stopPolling();
    };
  }, [
    handleFocusIn,
    handleFocusOut,
    handleTouchStart,
    handleVisualViewportResize,
    handleVirtualKeyboardGeometryChange,
    handleWindowResize,
    stopPolling,
  ]);

  // Determine keyboard visibility:
  // - If we have viewport APIs, use detected keyboard height
  // - Otherwise, fall back to focus state on mobile
  let isKeyboardVisible = false;
  if (hasViewportAPI.current) {
    isKeyboardVisible = keyboardHeight > 100;
  } else if (isMobile.current) {
    isKeyboardVisible = isInputFocused;
  }

  const value = useMemo(
    () => ({
      isKeyboardVisible,
      keyboardHeight,
      isInputFocused,
    }),
    [isKeyboardVisible, keyboardHeight, isInputFocused]
  );

  return <KeyboardContext.Provider value={value}>{children}</KeyboardContext.Provider>;
}

export default KeyboardContext;
